import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Link2, Copy, Loader2, ChevronDown, FileCheck2, Send, Lock, Unlock } from "lucide-react";
import { BulkBar, RowActions, RowCheck, useBulkSelect } from "@/components/AdminBulkActions";

const PRODUCTS = [
  { code: "rc_economia_solidale", label: "RC Economia Solidale", route: "/quota/nuovo" },
  { code: "mrv_volontariato", label: "Tutela Multirischi del Volontariato", route: "/quota/mrv" },
  { code: "infortuni_economia_solidale", label: "Infortuni per l'Economia Solidale", route: "/quota/infortuni" },
  { code: "cat_nat_impresa", label: "CAT NAT Assimoco", route: "/quota/catnat" },
  { code: "multirischi_beni", label: "Tutela Multirischi Danni ai Beni", route: "/quota/multirischi" },
];
const STATUS = {
  inviato: { label: "In attesa dati cliente", cls: "bg-amber-100 text-amber-800" },
  compilato: { label: "Dati ricevuti", cls: "bg-emerald-100 text-emerald-700" },
  quotato: { label: "Quotazione avviata", cls: "bg-forest/10 text-forest" },
};

export default function IntakeManager() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role && ["agency_admin", "agency_operator"].includes(user.role);
  const canPF = user?.role && ["agency_admin", "agency_operator", "partner_collab_AA"].includes(user.role);
  const [items, setItems] = useState([]);
  const [product, setProduct] = useState("rc_economia_solidale");
  const [creating, setCreating] = useState(false);
  const [pfLink, setPfLink] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [busy, setBusy] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const sel = useBulkSelect();

  const load = () => api.get(`/intakes${isAdmin && showArchived ? "?include_archived=true" : ""}`).then(({ data }) => { setItems(data); sel.clear(); });
  useEffect(() => { load(); }, [showArchived]); // eslint-disable-line

  const create = async () => {
    // Protezione Famiglia: non usa il modulo dati standard ma la pagina pacchetti pubblica,
    // intestata al partner tramite ?ref=<codice_collab>. L'adesione avviene sul flusso PF esistente.
    if (product === "protezione_famiglia") {
      const ref = user?.codice_collab ? `?ref=${encodeURIComponent(user.codice_collab)}` : "";
      const url = `${window.location.origin}/protezione-famiglia${ref}`;
      setPfLink(url);
      try { await navigator.clipboard.writeText(url); toast.success("Link Protezione Famiglia copiato negli appunti"); }
      catch { toast.success("Link Protezione Famiglia generato"); }
      return;
    }
    setCreating(true);
    try { await api.post("/intakes", { product_code: product }); toast.success("Link creato"); await load(); }
    catch (e) { toast.error(apiError(e)); } finally { setCreating(false); }
  };

  const publicUrl = (token) => `${window.location.origin}/modulo/${token}`;
  const copy = (token) => { navigator.clipboard.writeText(publicUrl(token)); toast.success("Link copiato negli appunti"); };
  const toggleChiuso = async (it) => {
    setBusy(it.id);
    try {
      await api.post(`/intakes/${it.id}/chiudi`, { chiuso: !it.chiuso });
      toast.success(it.chiuso ? "Link riaperto" : "Link chiuso — non più compilabile dal cliente");
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const genera = async (it) => {
    setBusy(it.id);
    try {
      const { data } = await api.post(`/intakes/${it.id}/genera-quotazione`);
      const base = isAdmin ? "/admin" : "";
      const route = PRODUCTS.find((p) => p.code === data.product_code)?.route || "/quota";
      toast.success("Anagrafica creata — completa la quotazione");
      navigate(`${base}${route}?anagrafica=${data.anagrafica_id}&intake=${it.id}`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-semibold text-forest">Link cliente</h1>
        <p className="mt-1 text-muted-foreground">Invia a un cliente un link per raccogliere i dati e preparare la proposta.</p>
      </div>

      <Card className="rounded-xl border-border/60 p-5">
        <h2 className="mb-3 font-heading text-lg font-semibold text-forest">Crea nuovo link</h2>
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[280px] flex-1">
            <Select value={product} onValueChange={setProduct}>
              <SelectTrigger data-testid="intake-product-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PRODUCTS.map((p) => <SelectItem key={p.code} value={p.code}>{p.label}</SelectItem>)}
                {canPF && <SelectItem value="protezione_famiglia" data-testid="intake-product-pf">Protezione Famiglia — Eticapro Infortuni Assimoco</SelectItem>}
              </SelectContent>
            </Select>
          </div>
          <Button data-testid="intake-create-button" onClick={create} disabled={creating} className="gap-1.5 bg-terracotta hover:bg-terracotta-hover">
            {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />} Genera link
          </Button>
        </div>
        {pfLink && (
          <div className="mt-4 rounded-lg border border-forest/20 bg-sage/40 p-3" data-testid="pf-link-box">
            <p className="text-xs font-medium text-forest/70">Link Protezione Famiglia (pagina pacchetti, intestato a te)</p>
            <div className="mt-1.5 flex items-center gap-2">
              <code className="flex-1 truncate rounded bg-white/70 px-2 py-1.5 text-xs text-forest">{pfLink}</code>
              <Button size="sm" variant="outline" className="h-8 gap-1.5" data-testid="pf-link-copy"
                onClick={() => { navigator.clipboard.writeText(pfLink); toast.success("Link copiato"); }}>
                <Copy className="h-3.5 w-3.5" /> Copia
              </Button>
            </div>
          </div>
        )}
      </Card>

      <Card className="rounded-xl border-border/60 p-5">
        <div className="mb-4 flex items-center gap-3">
          {isAdmin && (
            <Checkbox checked={items.length > 0 && sel.count >= items.length}
              onCheckedChange={() => sel.toggleAll(items.map((x) => x.id))} data-testid="select-all" aria-label="Seleziona tutti" />
          )}
          <h2 className="font-heading text-lg font-semibold text-forest">I miei link ({items.length})</h2>
        </div>
        {isAdmin && (
          <div className="mb-4">
            <BulkBar endpoint="/admin/intakes/bulk" selected={sel.selected} onDone={load}
              showArchived={showArchived} onToggleArchived={() => setShowArchived((s) => !s)} entityLabel="link cliente" />
          </div>
        )}
        {items.length === 0 && <p className="text-sm text-muted-foreground">Nessun link creato.</p>}
        <div className="space-y-3">
          {items.map((it) => (
            <div key={it.id} data-testid={`intake-row-${it.id}`} className="rounded-lg border border-border/60 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-3">
                  {isAdmin && <RowCheck id={it.id} selected={sel.isSelected(it.id)} onToggle={sel.toggle} />}
                  <div>
                    <div className="font-medium text-forest">{it.product_name}</div>
                    <div className="text-xs text-muted-foreground">Creato da {it.created_by_name}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${it.chiuso ? "bg-slate-200 text-slate-600" : STATUS[it.status]?.cls}`}>{it.chiuso ? "Link chiuso" : (STATUS[it.status]?.label || it.status)}</span>
                  {it.status === "inviato" && !it.chiuso && (
                    <Button data-testid={`intake-copy-${it.id}`} size="sm" variant="outline" onClick={() => copy(it.token)} className="h-8 gap-1.5">
                      <Copy className="h-3.5 w-3.5" /> Copia link
                    </Button>
                  )}
                  {it.status === "inviato" && (
                    <Button data-testid={`intake-chiudi-${it.id}`} size="sm" variant="ghost" onClick={() => toggleChiuso(it)} disabled={busy === it.id}
                      className={`h-8 gap-1.5 ${it.chiuso ? "text-forest" : "text-muted-foreground"}`}>
                      {busy === it.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : it.chiuso ? <Unlock className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />}
                      {it.chiuso ? "Riapri" : "Chiudi"}
                    </Button>
                  )}
                  {it.status !== "inviato" && (
                    <Button size="sm" variant="ghost" data-testid={`intake-toggle-${it.id}`} onClick={() => setExpanded(expanded === it.id ? null : it.id)} className="h-8 gap-1">
                      Dati <ChevronDown className={`h-3.5 w-3.5 transition-transform ${expanded === it.id ? "rotate-180" : ""}`} />
                    </Button>
                  )}
                  {isAdmin && <RowActions endpoint="/admin/intakes/bulk" id={it.id} archived={showArchived} onDone={load} />}
                </div>
              </div>

              {it.status === "inviato" && !it.chiuso && (
                <div className="mt-2 truncate rounded bg-sage/40 px-2 py-1 text-xs text-forest/70">{publicUrl(it.token)}</div>
              )}

              {expanded === it.id && it.client_data && (
                <div className="mt-3 rounded-lg bg-sage/30 p-3" data-testid={`intake-report-${it.id}`}>
                  <dl className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
                    {Object.entries(it.client_data).filter(([k, v]) => v !== "" && v != null && typeof v !== "object").map(([k, v]) => (
                      <div key={k} className="flex justify-between gap-2 border-b border-border/40 py-1">
                        <dt className="text-muted-foreground">{k.replace(/_/g, " ")}</dt>
                        <dd className="text-right font-medium text-forest">{String(v)}</dd>
                      </div>
                    ))}
                  </dl>
                  {it.client_data.sinistri && it.client_data.sinistri.ha_sinistri != null && (
                    <div className="mt-2 rounded-lg border border-border/40 bg-white/60 p-2 text-xs" data-testid={`intake-sinistri-${it.id}`}>
                      <span className="font-medium text-forest">Dichiarazione sinistri: </span>
                      {it.client_data.sinistri.ha_sinistri
                        ? (it.client_data.sinistri.elenco || []).map((s, i) => (
                            <span key={i} className="text-forest/80">{i > 0 ? "; " : ""}{[s.anno, s.tipo, s.importo ? `€ ${s.importo}` : "", s.stato].filter(Boolean).join(" · ")}</span>
                          ))
                        : <span className="text-emerald-700">nessun sinistro dichiarato</span>}
                    </div>
                  )}
                  {it.client_data.idd && it.client_data.idd.adeguatezza && (
                    <div className="mt-2 rounded-lg border border-border/40 bg-white/60 p-2 text-xs" data-testid={`intake-idd-${it.id}`}>
                      <span className="font-medium text-forest">Adeguatezza (IDD): </span>
                      <span className={it.client_data.idd.adeguatezza === "adeguato" ? "text-emerald-700" : "text-amber-700"}>
                        {it.client_data.idd.adeguatezza === "adeguato" ? "adeguato ai bisogni" : "non adeguato"}
                      </span>
                    </div>
                  )}
                  {it.status === "compilato" && (
                    <Button data-testid={`intake-genera-${it.id}`} onClick={() => genera(it)} disabled={busy === it.id}
                      className="mt-3 gap-1.5 bg-forest hover:bg-forest-hover">
                      {busy === it.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileCheck2 className="h-4 w-4" />} Genera quotazione
                    </Button>
                  )}
                  {it.status === "quotato" && it.anagrafica_id && (
                    <Button size="sm" variant="outline" data-testid={`intake-riprendi-${it.id}`} onClick={() => {
                      const base = isAdmin ? "/admin" : "";
                      const route = PRODUCTS.find((p) => p.code === it.product_code)?.route || "/quota";
                      navigate(`${base}${route}?anagrafica=${it.anagrafica_id}&intake=${it.id}`);
                    }} className="mt-3 gap-1.5"><Send className="h-3.5 w-3.5" /> Riprendi quotazione</Button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
